import { formatUnits } from 'viem';
import { TickMath } from '@uniswap/v3-sdk';
import { Q128 } from '@/utils/constants';

const Q96: bigint = 2n ** 96n;

export function tickToPrice(tick: number, decimal0: number, decimal1: number) {
  if (tick === TickMath.MIN_TICK) return 0;
  if (tick === TickMath.MAX_TICK) return Infinity;
  return 1.0001 ** tick / 10 ** (decimal1 - decimal0);
}

function getTickAtSqrtPrice(sqrtPriceX96: bigint): number {
  const ratio = Number(sqrtPriceX96) / Number(Q96);
  const ratioSquared = ratio * ratio;
  const logRatio = Math.log(Number(ratioSquared)); // conversion to Number for Math.log
  const tick = Math.floor(logRatio / Math.log(1.0001));
  return tick;
}

function countDecimals(value: number) {
  if (Math.floor(value) === value) return 0;
  // handle scientific notation
  if (value.toString().includes('e')) {
    // Split the number into base and exponent parts
    let [base, exponent] = value.toString().split('e');
    // Count the number of decimal places in the base
    let baseDecimals = base.includes('.') ? base.split('.')[1].length : 0;
    // Adjust the count with the exponent
    let totalDecimals = baseDecimals - parseInt(exponent, 10);
    return totalDecimals;
  }
  return value.toString().split('.')[1].length || 0;
}

// From
// https://blog.uniswap.org/uniswap-v3-math-primer-2
export function getTokenAmounts({
  liquidity,
  sqrtPriceX96,
  tickLow,
  tickHigh,
  decimal0,
  decimal1,
}: {
  liquidity: bigint;
  sqrtPriceX96: bigint;
  tickLow: number;
  tickHigh: number;
  decimal0: number;
  decimal1: number;
}): [string, string] {
  let sqrtRatioA = Math.sqrt(1.0001 ** tickLow);
  let sqrtRatioB = Math.sqrt(1.0001 ** tickHigh);
  let currentTick = getTickAtSqrtPrice(sqrtPriceX96);
  let sqrtPrice = Number(sqrtPriceX96) / Number(Q96);

  let amount0 = 0n;
  let amount1 = 0n;
  if (currentTick < tickLow) {
    amount0 = liquidity * BigInt((sqrtRatioB - sqrtRatioA) / (sqrtRatioA * sqrtRatioB));
  } else if (currentTick >= tickHigh) {
    amount1 = liquidity * BigInt(sqrtRatioB - sqrtRatioA);
  } else if (currentTick >= tickLow && currentTick < tickHigh) {
    const calc0 = (sqrtRatioB - sqrtPrice) / (sqrtPrice * sqrtRatioB);
    amount0 = BigInt(
      Math.floor(
        Number(
          (liquidity * BigInt(calc0 * 10 ** countDecimals(calc0))) /
            BigInt(10 ** countDecimals(calc0)),
        ),
      ),
    );
    const calc1 = sqrtPrice - sqrtRatioA;
    amount1 = BigInt(
      Math.floor(
        Number(
          (liquidity * BigInt(calc1 * 10 ** countDecimals(calc1))) /
            BigInt(10 ** countDecimals(calc1)),
        ),
      ),
    );
  }

  return [formatUnits(amount0, decimal0), formatUnits(amount1, decimal1)];
}

export const getPositionUncollectedFees = ({
  feeGrowthInsideX128,
  feeGrowthInsideLastX128,
  liquidity,
}: {
  feeGrowthInsideX128: bigint | undefined;
  feeGrowthInsideLastX128: bigint | undefined;
  liquidity: bigint | undefined;
}): bigint => {
  if (
    feeGrowthInsideX128 === undefined ||
    feeGrowthInsideLastX128 === undefined ||
    liquidity === undefined
  ) {
    return 0n;
  }
  return ((feeGrowthInsideX128 - feeGrowthInsideLastX128) * liquidity) / Q128;
};
